import { File as FileIcon, FileSpreadsheet, FileText } from 'lucide-react'
import type { Media } from '~/payload-types'

export function normalizeInternalMediaUrl(url?: string | null): string | null {
  if (!url) return null

  const urlWithoutHash = url.split('#')[0] ?? url

  if (urlWithoutHash.startsWith('/')) {
    return urlWithoutHash
  }

  try {
    const parsed = new URL(urlWithoutHash)
    const sameOriginPath = `${parsed.pathname}${parsed.search}`

    if (sameOriginPath.startsWith('/api/media/file/') || sameOriginPath.startsWith('/media/')) {
      return sameOriginPath
    }
  } catch {
    // ignore
  }

  return urlWithoutHash
}

function isInternalMediaPath(url: string) {
  return url.startsWith('/api/media/file/') || url.startsWith('/media/')
}

function appendQueryParam(url: string, key: string, value: string) {
  try {
    const u = new URL(url, 'http://localhost')
    u.searchParams.set(key, value)

    return url.startsWith('http') ? u.toString() : `${u.pathname}${u.search}`
  } catch {
    const sep = url.includes('?') ? '&' : '?'
    return `${url}${sep}${encodeURIComponent(key)}=${encodeURIComponent(value)}`
  }
}

export function getVersionedAttachmentUrl(
  attachment: Pick<Media, 'id' | 'url' | 'updatedAt'>,
): string | null {
  const urlBase = normalizeInternalMediaUrl(attachment.url)

  if (!urlBase) {
    return null
  }

  return isInternalMediaPath(urlBase)
    ? appendQueryParam(urlBase, 'v', attachment.updatedAt ?? String(attachment.id))
    : urlBase
}

export function getFileIcon(filename?: string) {
  if (!filename) return <FileIcon className="w-4 h-4" />

  const ext = filename.split('.').pop()?.toLowerCase()

  switch (ext) {
    case 'xlsx':
    case 'xls':
    case 'csv':
      return <FileSpreadsheet className="w-4 h-4 text-green-600" />
    case 'pdf':
      return <FileText className="w-4 h-4 text-red-500" />
    case 'doc':
    case 'docx':
      return <FileText className="w-4 h-4 text-blue-500" />
    case 'ppt':
    case 'pptx':
      return <FileText className="w-4 h-4 text-orange-500" />
    default:
      return <FileIcon className="w-4 h-4 text-gray-500" />
  }
}

export function getAttachmentSecondaryText(
  attachment: Pick<Media, 'annotations' | 'alt'>,
): string | null {
  return attachment.annotations?.[0]?.label ?? attachment.alt ?? null
}
